import React from 'react';
import {
  ActionDropDown, Badge, Dropdown, Portal, Skeleton,
  useModal,
} from '@doveit/bricks';
import { useTheme } from 'styled-components';
import { Property } from '../../../providers/api/dtos';
import { useAgentByPropertyId } from '../../../hooks/use-agent/useAgent';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import { useComputedPropertyStatus } from '../../hooks/use-computed-property-status/useComputedPropertyStatus';
import { Status } from '../../../domain/types';
import PreviewPropertyAction from '../preview-property-action/PreviewPropertyAction';
import PublishPropertyAction from '../publish-property-action/PublishPropertyAction';
import WithdrawPropertyAction from '../withdraw-property-action/WithdrawPropertyAction';
import DraftPropertyAction from '../draft-property-action/DraftPropertyAction';
import NegotiationPropertyAction from '../negotiation-property-action/NegotiationPropertyAction';
import { useCurrentAgentIsSameAgentOrManager } from '../../../agent/hooks/use-current-agent-is-same-agent-or-manager/useCurrentAgentIsSameAgentOrManager';
import MarkAssignmentAsCollectedAction from '../../../assignment/containers/mark-assignment-as-collected-action/MarkAssignmentAsCollectedAction';
import useAssignmentByPropertyId from '../../hooks/use-assignment-by-property-id/useAssignmentByPropertyId';
import { computedStatusActionColor, computedStatusActionIcon, computedStatusLabels } from './constants';
import useUserPermissions from '../../../hooks/use-user-permissions/useUserPermissions';
import MarketingOpportunitiesModal from '../../../marketing/components/marketing-opportunities-modal/MarketingOpportunitiesModal';

export interface UpdatePropertyStatusProps {
  property: Property,
  onSuccess?: (updatedProperty: Property) => void,
  emphasis?: 'low' | 'high',
  size?: 'XS' | 'S',
  showIcon?: boolean,
}

const UpdatePropertyStatus: React.FC<UpdatePropertyStatusProps> = ({
  property,
  onSuccess,
  emphasis = 'low',
  size = 'S',
  showIcon = false,
}) => {
  const theme = useTheme();
  const { userIsAdmin, userIsContentEditor, userIsFinance } = useRBAC();
  const userPermissions = useUserPermissions(['marketing']);
  const { data: propertyAgent, error: propertyAgentError, isLoading: propertyAgentIsLoading } = useAgentByPropertyId(property.id);
  const {
    data: assignment, error: assignmentError, isLoading: isAssignmentLoading, mutate: mutateAssignment,
  } = useAssignmentByPropertyId(property.id);
  const marketingOpportunitiesModal = useModal<{ property: Property }>();

  const isPropertyAgentOrManager = useCurrentAgentIsSameAgentOrManager(propertyAgent?.id);

  const { data: computedPropertyStatus, errors: computedPropertyStatusError, isLoading: isComputedPropertyStatusLoading } = useComputedPropertyStatus(property);

  const showAsBadge = React.useMemo(
    () => !computedPropertyStatus?.canBeUpdated || !(userIsAdmin || userIsContentEditor || userIsFinance || isPropertyAgentOrManager),
    [computedPropertyStatus?.canBeUpdated, isPropertyAgentOrManager, userIsAdmin, userIsContentEditor, userIsFinance],
  );

  const canBeUpdatedTo = computedPropertyStatus?.canBeUpdatedTo;

  const onPropertyPublish = React.useCallback((liveProperty: Property) => {
    if (userPermissions.marketing.canSeeSuggestions) {
      marketingOpportunitiesModal.open({ property: liveProperty });
    } else {
      onSuccess?.(liveProperty);
    }
  }, [marketingOpportunitiesModal, onSuccess, userPermissions.marketing.canSeeSuggestions]);

  const marketingTriggers = React.useMemo(() => [
    {
      type: 'property-recently-published' as const,
      propertyId: property.id!,
      agentId: propertyAgent?.id,
    },
  ], [property.id, propertyAgent?.id]);

  if (propertyAgentError || computedPropertyStatusError || assignmentError) {
    return null;
  }

  if (propertyAgentIsLoading || isComputedPropertyStatusLoading || isAssignmentLoading) {
    return (
      <Skeleton
        width="7rem"
        height={theme.remHeight[size]}
        aria-label="Stato dell'immobile in caricamento"
      />
    );
  }

  return (
    <>
      {showAsBadge ? (
        <Badge
          label={computedStatusLabels[computedPropertyStatus!.status]}
          color={computedStatusActionColor[computedPropertyStatus!.status]}
          icon={showIcon ? computedStatusActionIcon[computedPropertyStatus!.status] : undefined}
          emphasis={emphasis}
          size={size}
          aria-label="Badge per mostrare lo stato dell'immobile"
        />
      ) : (
        <ActionDropDown
          label={computedStatusLabels[computedPropertyStatus!.status]}
          color={computedStatusActionColor[computedPropertyStatus!.status]}
          iconLeft={showIcon ? { path: computedStatusActionIcon[computedPropertyStatus!.status] } : undefined}
          emphasis={emphasis}
          size={size}
          aria-label="Selezione per aggiornare lo stato dell'immobile"
        >
          {canBeUpdatedTo?.(Status.PROPOSTA) && (
            <NegotiationPropertyAction
              property={property}
              aria-label="Pulsante per mettere l'immobile in trattativa"
              onSuccess={onSuccess}
            >
              {({ onNegotiation }) => (
                <Dropdown.Option
                  label="Metti in trattativa"
                  onClick={onNegotiation}
                />
              )}
            </NegotiationPropertyAction>
          )}

          {canBeUpdatedTo?.(Status.LIVE) && (
            <PublishPropertyAction
              aria-label="Pulsante di pubblicazione immobile"
              property={property}
              onSuccess={onPropertyPublish}
            >
              {({ publish }) => (
                <Dropdown.Option
                  label={property.status === Status.PROPOSTA ? 'Nascondi trattativa' : 'Pubblica'}
                  onClick={publish}
                />
              )}
            </PublishPropertyAction>
          )}

          {canBeUpdatedTo?.(Status.ANTEPRIMA) && (
            <PreviewPropertyAction
              aria-label="Pulsante di anteprima immobile"
              property={property}
              onSuccess={onSuccess}
            >
              {({ preview }) => (
                <Dropdown.Option
                  label="Pubblica in anteprima"
                  onClick={preview}
                />
              )}
            </PreviewPropertyAction>
          )}

          {canBeUpdatedTo?.(Status.DRAFT) && (
            <DraftPropertyAction
              aria-label="Pulsante di bozza immobile"
              property={property}
              onSuccess={onSuccess}
            >
              {({ onDraft: onDraftSuccess }) => (
                <Dropdown.Option
                  label={property.status === Status.RITIRATO ? 'Ripristina' : 'Sposta in bozza'}
                  onClick={onDraftSuccess}
                />
              )}
            </DraftPropertyAction>
          )}

          {canBeUpdatedTo?.(Status.RITIRATO) && (
            <WithdrawPropertyAction
              aria-label="Pulsante di ritiro immobile"
              property={property}
              onSuccess={onSuccess}
            >
              {({ withdraw }) => (
                <Dropdown.Option
                  label="Ritira"
                  onClick={withdraw}
                />
              )}
            </WithdrawPropertyAction>
          )}

          {canBeUpdatedTo?.('COLLECTED') && (
            <MarkAssignmentAsCollectedAction
              assignmentId={assignment!.id}
              aria-label="Pulsante per incassare il pagamento"
              onSuccess={mutateAssignment}
            >
              {({ onClick }) => (
                <Dropdown.Option
                  label="Incassa"
                  onClick={onClick}
                />
              )}
            </MarkAssignmentAsCollectedAction>
          )}
        </ActionDropDown>
      )}

      {userPermissions.marketing.canSeeSuggestions && marketingOpportunitiesModal.data && (
        <Portal>
          <MarketingOpportunitiesModal
            triggers={marketingTriggers}
            {...marketingOpportunitiesModal}
            close={() => {
              marketingOpportunitiesModal.close();

              onSuccess?.(marketingOpportunitiesModal.data!.property);
            }}
          />
        </Portal>
      )}
    </>
  );
};

export default UpdatePropertyStatus;
